"use client";

import { useState } from 'react';
import ConfigCard from './ConfigCard';
import type { PageTexts } from '@/lib/siteTexts';

interface ResourceType {
  id: string;
  name: string;
  unit: string;
  icon: string;
}

interface ConfigurationResource {
  id: string;
  resource_type_id: string;
  quantity: number;
  resource_types: ResourceType | null;
}

interface Configuration {
  id: string;
  name: string;
  group_id: string | null;
  description: string | null;
  price: number | null;
  display_order: number;
  configuration_resources: ConfigurationResource[];
}

interface ConfigurationGroup {
  id: string;
  name: string;
  description: string | null;
  display_order: number;
}

interface Props {
  groups: ConfigurationGroup[];
  configurations: Configuration[];
  texts: PageTexts<'configurations'>;
}

export default function ConfigurationsFilter({ groups, configurations, texts: t }: Props) {
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  const filteredConfigs = selectedGroupId === null
    ? configurations
    : configurations.filter(c => c.group_id === selectedGroupId);

  // When "Все" selected, group by group; when specific group selected, flat list
  const selectedGroup = selectedGroupId ? groups.find(g => g.id === selectedGroupId) : null;

  const groupedView = selectedGroupId === null;

  const configsByGroup = groupedView
    ? groups.map(g => ({
        group: g,
        configs: configurations.filter(c => c.group_id === g.id),
      })).filter(item => item.configs.length > 0)
    : [];

  const ungrouped = groupedView
    ? configurations.filter(c => !c.group_id || !groups.find(g => g.id === c.group_id))
    : [];

  return (
    <div>
      {/* Filter Tabs */}
      {groups.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-10 justify-center">
          <button
            onClick={() => setSelectedGroupId(null)}
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 border ${
              selectedGroupId === null
                ? 'bg-[#00D4FF] border-[#00D4FF] text-[#050B18]'
                : 'bg-transparent border-[rgba(79,124,255,0.3)] text-[#8BA3CC] hover:border-[#00D4FF] hover:text-white'
            }`}
          >
            {t['list.all']}
          </button>
          {groups.map(group => (
            <button
              key={group.id}
              onClick={() => setSelectedGroupId(group.id)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 border ${
                selectedGroupId === group.id
                  ? 'bg-[#00D4FF] border-[#00D4FF] text-[#050B18]'
                  : 'bg-transparent border-[rgba(79,124,255,0.3)] text-[#8BA3CC] hover:border-[#00D4FF] hover:text-white'
              }`}
            >
              {group.name}
            </button>
          ))}
        </div>
      )}

      {/* Configurations */}
      {filteredConfigs.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          {t['list.empty']}
        </div>
      ) : groupedView ? (
        <div className="space-y-14">
          {configsByGroup.map(({ group, configs }) => (
            <section key={group.id}>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-white">{group.name}</h2>
                {group.description && (
                  <p className="text-[#8BA3CC] mt-1">{group.description}</p>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {configs.map(config => (
                  <ConfigCard key={config.id} config={config} texts={t} />
                ))}
              </div>
            </section>
          ))}
          {ungrouped.length > 0 && (
            <section>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-white">{t['list.ungrouped']}</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {ungrouped.map(config => (
                  <ConfigCard key={config.id} config={config} texts={t} />
                ))}
              </div>
            </section>
          )}
        </div>
      ) : (
        <div>
          {selectedGroup && (
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-white">{selectedGroup.name}</h2>
              {selectedGroup.description && (
                <p className="text-[#8BA3CC] mt-1">{selectedGroup.description}</p>
              )}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredConfigs.map(config => (
              <ConfigCard key={config.id} config={config} texts={t} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
